import jsPDF from "jspdf"
import { QUALITY_RANGES, type Unit, type LiquidType, type LyeType, type SoapCalculations } from "@/data/oilDatabase"
import { barWeightForUnit, calculatePricing, type PricingInputs } from "@/utils/pricing"

interface ExportPdfParams {
  calculations: SoapCalculations
  unit: Unit
  batchSize: number
  superfat: number
  lyeType: LyeType
  liquidType: LiquidType
  fragrancePercent: number
  pricing?: PricingInputs
}

export function exportToPDF({
  calculations,
  unit,
  batchSize,
  superfat,
  lyeType,
  liquidType,
  fragrancePercent,
  pricing,
}: ExportPdfParams) {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 15
  const contentWidth = pageWidth - margin * 2
  let yPosition = 20

  // Header
  doc.setFontSize(24)
  doc.setFont("helvetica", "bold")
  doc.text("Calculator Soap - Recipe Card", pageWidth / 2, yPosition, { align: "center" })

  yPosition += 15
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.text("Professional Soap Recipe Dashboard", pageWidth / 2, yPosition, { align: "center" })

  yPosition += 15

  // Recipe Settings Box
  doc.setFillColor(240, 253, 244)
  doc.rect(15, yPosition, pageWidth - 30, 40, "F")
  doc.setDrawColor(167, 243, 208)
  doc.rect(15, yPosition, pageWidth - 30, 40, "S")

  yPosition += 10
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Recipe Settings", 20, yPosition)

  yPosition += 8
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.text(`Batch Size: ${batchSize} ${unit}`, 20, yPosition)
  doc.text(`Superfat: ${superfat}%`, 105, yPosition)

  yPosition += 6
  doc.text(`Lye Type: ${lyeType}`, 20, yPosition)
  doc.text(`Liquid: ${liquidType}`, 105, yPosition)

  yPosition += 6
  doc.text(`Fragrance: ${fragrancePercent}%`, 20, yPosition)
  doc.text(`Unit: ${unit}`, 105, yPosition)

  yPosition += 15

  // Oils Section
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Oil Blend", 20, yPosition)

  yPosition += 8
  doc.setFontSize(9)
  doc.setFont("helvetica", "bold")
  doc.text("Oil Name", 20, yPosition)
  doc.text("Percentage", 100, yPosition)
  doc.text(`Weight (${unit})`, 145, yPosition)

  yPosition += 2
  doc.setDrawColor(200, 200, 200)
  doc.line(15, yPosition, pageWidth - 15, yPosition)

  yPosition += 6
  doc.setFont("helvetica", "normal")

  calculations.oilsWithCalculatedWeights.forEach((oil) => {
    if (yPosition > 270) {
      doc.addPage()
      yPosition = 20
    }

    doc.text(oil.oil, 20, yPosition)
    doc.text(`${oil.percentage.toFixed(1)}%`, 100, yPosition)
    doc.text(`${oil.weight.toFixed(1)}`, 145, yPosition)
    yPosition += 6
  })

  yPosition += 5

  // Required Ingredients Box
  doc.setFillColor(240, 253, 244)
  const boxHeight = 45
  doc.rect(15, yPosition, pageWidth - 30, boxHeight, "F")
  doc.setDrawColor(167, 243, 208)
  doc.rect(15, yPosition, pageWidth - 30, boxHeight, "S")

  yPosition += 10
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Required Ingredients", 20, yPosition)

  yPosition += 8
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.text("Total Oils:", 20, yPosition)
  doc.text(`${calculations.totalOilWeight.toFixed(1)} ${unit}`, 150, yPosition, { align: "right" })

  yPosition += 6
  doc.text(`${lyeType}:`, 20, yPosition)
  doc.text(`${calculations.totalLyeRequired.toFixed(1)} ${unit}`, 150, yPosition, { align: "right" })

  yPosition += 6
  doc.text(`Liquid (${liquidType}):`, 20, yPosition)
  doc.text(`${calculations.totalLiquid.toFixed(1)} ${unit}`, 150, yPosition, { align: "right" })

  yPosition += 6
  doc.text(`Fragrance (${fragrancePercent}%):`, 20, yPosition)
  doc.text(`${calculations.fragranceLoad.toFixed(1)} ${unit}`, 150, yPosition, { align: "right" })

  yPosition += 8
  doc.setFont("helvetica", "bold")
  doc.text("Total Batch Weight:", 20, yPosition)
  doc.setTextColor(16, 185, 129)
  doc.text(`${calculations.totalBatchWeight.toFixed(1)} ${unit}`, 150, yPosition, { align: "right" })
  doc.setTextColor(0, 0, 0)

  yPosition += 15

  // Cost Breakdown & Pricing Recommendations
  if (pricing || calculations.oilsWithCalculatedWeights.some((o) => o.costPerUnit > 0)) {
    if (yPosition > 200) {
      doc.addPage()
      yPosition = 20
    }

    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.text("Cost Breakdown & Pricing", 20, yPosition)

    yPosition += 8
    doc.setFontSize(9)
    doc.setFont("helvetica", "bold")
    doc.text("Oil", 20, yPosition)
    doc.text("Cost", 150, yPosition, { align: "right" })

    yPosition += 2
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, yPosition, pageWidth - margin, yPosition)

    yPosition += 6
    doc.setFont("helvetica", "normal")

    calculations.oilsWithCalculatedWeights.forEach((oil) => {
      if (oil.costPerUnit <= 0) return
      if (yPosition > 270) {
        doc.addPage()
        yPosition = 20
      }
      doc.text(oil.oil, 20, yPosition)
      doc.text(`$${(oil.weight * oil.costPerUnit).toFixed(2)}`, 150, yPosition, { align: "right" })
      yPosition += 6
    })

    yPosition += 2
    doc.setFont("helvetica", "bold")
    doc.text("Total Oil Cost:", 20, yPosition)
    doc.text(`$${calculations.totalCost.toFixed(2)}`, 150, yPosition, { align: "right" })
    yPosition += 6

    if (pricing) {
      const result = calculatePricing(
        calculations.totalCost,
        calculations.totalBatchWeight,
        unit,
        pricing
      )

      if (yPosition > 210) {
        doc.addPage()
        yPosition = 20
      }

      yPosition += 8
      doc.setFillColor(240, 253, 244)
      doc.rect(margin, yPosition - 6, contentWidth, 66, "F")
      doc.setDrawColor(167, 243, 208)
      doc.rect(margin, yPosition - 6, contentWidth, 66, "S")

      doc.setFontSize(11)
      doc.setFont("helvetica", "bold")
      doc.text("Pricing Recommendations", 20, yPosition + 4)

      yPosition += 12
      doc.setFontSize(9)
      doc.setFont("helvetica", "normal")

      const rows: Array<[string, string]> = [
        ["Extra cost per batch (lye, fragrance, packaging):", `$${Number(pricing.extraCostPerBatch || 0).toFixed(2)}`],
        ["Total batch cost:", `$${result.batchTotalCost.toFixed(2)}`],
        ["Bar weight", `${barWeightForUnit(pricing.barWeightGrams, unit)} ${unit}`],
        ["Bars per batch", Number.isFinite(result.barsPerBatch) ? result.barsPerBatch.toFixed(1) : "\u2014"],
        ["Cost per bar", `$${result.costPerBar.toFixed(2)}`],
        ["Target margin", `${result.targetMargin}%`],
        ["Profit per bar", `$${result.profitPerBar.toFixed(2)}`],
        ["Suggested rounded price", `$${result.roundedPrice.toFixed(2)}`],
        ["Wholesale (50% of retail)", `$${result.wholesalePrice.toFixed(2)}`],
      ]

      rows.forEach(([label, value]) => {
        if (yPosition > 275) {
          doc.addPage()
          yPosition = 20
        }
        doc.text(label, 20, yPosition)
        doc.text(value, 150, yPosition, { align: "right" })
        yPosition += 5.5
      })

      doc.setFont("helvetica", "bold")
      doc.setFontSize(11)
      doc.text("Recommended retail price:", 20, yPosition + 2)
      doc.setTextColor(16, 185, 129)
      doc.text(`$${result.recommendedPrice.toFixed(2)} per bar`, 150, yPosition + 2, { align: "right" })
      doc.setTextColor(0, 0, 0)
      doc.setFont("helvetica", "normal")
      yPosition += 12

      doc.setFontSize(7)
      doc.setTextColor(110, 110, 110)
      doc.text("Retail = cost per bar / (1 - margin). Wholesale assumes 50% of retail.", 20, yPosition)
      doc.setTextColor(0, 0, 0)
      yPosition += 6
    }
  }

  yPosition += 12

  // Soap Quality Metrics
  if (yPosition > 230) {
    doc.addPage()
    yPosition = 20
  }

  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Soap Quality Metrics", 20, yPosition)

  yPosition += 8
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")

  const metrics = [
    { name: "Hardness", value: calculations.hardness, range: `${QUALITY_RANGES.hardness.min}-${QUALITY_RANGES.hardness.max}` },
    { name: "Cleansing", value: calculations.cleansing, range: `${QUALITY_RANGES.cleansing.min}-${QUALITY_RANGES.cleansing.max}` },
    { name: "Conditioning", value: calculations.conditioning, range: `${QUALITY_RANGES.conditioning.min}-${QUALITY_RANGES.conditioning.max}` },
    { name: "Bubbly Lather", value: calculations.bubbly, range: `${QUALITY_RANGES.bubbly.min}-${QUALITY_RANGES.bubbly.max}` },
    { name: "Creamy Lather", value: calculations.creamy, range: `${QUALITY_RANGES.creamy.min}-${QUALITY_RANGES.creamy.max}` },
  ]

  metrics.forEach((metric) => {
    doc.text(`${metric.name}:`, 20, yPosition)
    doc.text(`${metric.value.toFixed(0)} (Target: ${metric.range})`, 150, yPosition, { align: "right" })
    yPosition += 6
  })

  yPosition += 10

  // Checklist
  if (yPosition > 180) {
    doc.addPage()
    yPosition = 20
  }

  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Soap Making Checklist", 20, yPosition)

  yPosition += 8
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")

  const checklist = [
    "Weigh all oils and melt solid oils",
    "Weigh liquid in heat-safe container",
    "Carefully add lye to liquid (not liquid to lye!)",
    "Allow lye solution to cool to 100-110\u00B0F",
    "Combine oils and lye solution",
    "Blend to trace",
    "Add fragrance and mix thoroughly",
    "Pour into mold",
    "Insulate for 24 hours",
    "Unmold and cut after 24-48 hours",
    "Cure for 4-6 weeks",
  ]

  checklist.forEach((item) => {
    if (yPosition > 280) {
      doc.addPage()
      yPosition = 20
    }
    doc.text("\u2610", 20, yPosition)
    doc.text(item, 28, yPosition)
    yPosition += 6
  })

  yPosition += 10

  // Footer
  if (yPosition > 270) {
    doc.addPage()
    yPosition = 20
  }

  doc.setFontSize(8)
  doc.setFont("helvetica", "italic")
  doc.setTextColor(100, 100, 100)
  doc.text("Generated by Calculator Soap - Professional Soap Recipe Dashboard", pageWidth / 2, yPosition, { align: "center" })
  yPosition += 5
  doc.text("Always follow proper safety procedures when making soap.", pageWidth / 2, yPosition, { align: "center" })

  // Save PDF
  const fileName = `soap-recipe-${Date.now()}.pdf`
  doc.save(fileName)
}
